import Logotype from '@/components/elements/Logotype';
import ContainerLayout from '@/components/layouts/ContainerLayout';
import PageViewLayout from '@/components/layouts/PageViewLayout/PageViewLayout';
import BookAppointment from '@/components/modules/modals/BookAppointment/BookAppointment';
import { BookAppointmentProvider } from '@/components/modules/modals/BookAppointment/BookAppointment.hooks';
import SEO, { seoPropsFromBaseString } from '@/components/modules/SEO';
import { APP_ROUTES, ROUTE_BUILDER } from '@/constants/pages';
import { hasNoDefaultLocation } from '@/features/searchV2/components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.helpers';
import { SEARCH_CONTEXT } from '@/features/searchV2/constants/tracking';
import FakeSearchForm, {
  FakeSearchFormRef,
  OnLocationUpdate,
} from '@/features/searchV2/modules/FakeSearchForm/FakeSearchForm';
import { url } from '@/helpers';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { useAppDispatch } from '@/hooks';
import {
  DEFAULT_MY_LOCATION_KEYWORD,
  DEFAULT_SEARCH_KEYWORD,
  DEFAULT_SEARCH_LOCATION,
} from '@/hooks/search/useSearch.constants';
import { getLocationWithSlug } from '@/hooks/search/useSearch.helpers';
import { BaseSearchManagerOutput } from '@/hooks/search/useSearch.types';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import useMobileView from '@/hooks/useMobileView';
import useScrollToTop from '@/hooks/useScrollToTop';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useShouldPromptLocation } from '../../../../hooks/useShouldPromptLocation';
import NoSearchResults from '../../components/NoSearchResults';
import { createSerpCardClickHandlers } from '../../helpers';
import { isSmtOnlySearchEnabled } from '../../hooks/useSmtOnlyToggle';
import DealsSectionPreview, { DealsSectionPreviewSkeleton } from '../../modules/DealSectionPreview';
import { HeroSection } from './DealsLanding.components';
import { useDealsLandingPage } from './DealsLanding.hooks';

const DealsLanding = () => {
  const searchFormRef = useRef<FakeSearchFormRef>(null);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const intersectionTarget = useRef(null);
  const { isMobileView } = useMobileView();
  const intersectionObserver = useIntersectionObserver(intersectionTarget, {
    root: null,
    threshold: 0.1,
    initialPosition: 'inView',
  });
  const isIntersectingHeroSearchbar = intersectionObserver === 'inView';

  const { deals: preview, isLoading } = useDealsLandingPage();

  // Location chooser modal
  const { shouldPrompt, isChecking, resolvedLocation } = useShouldPromptLocation();
  const [selectedLocation, setSelectedLocation] = useState<Parameters<OnLocationUpdate>[0] | null>(null);

  useScrollToTop();

  useEffect(() => {
    if (isChecking) {
      return;
    }

    if (resolvedLocation) {
      handleLocationUpdate({ location: resolvedLocation.label });
      setSelectedLocation({ location: resolvedLocation.label });
    }
  }, [shouldPrompt, isChecking, resolvedLocation]);

  const handleLocationUpdate = useCallback((params: { location: string; lat?: string; lon?: string }) => {
    setSelectedLocation({ ...params });
    searchFormRef.current?.setLocation(params);
  }, []);

  const handleCategoryClick = (keyword: string) => {
    const indexableLocation = getLocationWithSlug(hasNoDefaultLocation() ? undefined : selectedLocation?.location);

    const query = new URLSearchParams();
    const state = { searchContext: SEARCH_CONTEXT.DEALS_KEYWORD_SHORTCUTS };
    const smtOnly = isSmtOnlySearchEnabled('deals');

    if (selectedLocation?.lat && selectedLocation?.lon && indexableLocation?.slug === DEFAULT_MY_LOCATION_KEYWORD) {
      query.set('lat', selectedLocation.lat);
      query.set('lon', selectedLocation.lon);
    }

    /**
     * handle indexable location
     */
    if (indexableLocation?.slug) {
      const search = query.toString() || undefined;

      if (smtOnly) {
        history.push({ pathname: ROUTE_BUILDER.sistaMinutenSerp(keyword, indexableLocation.slug), search, state });
      } else {
        history.push({ pathname: ROUTE_BUILDER.dealsSerpPreview(keyword, indexableLocation.slug), search, state });
      }
      return;
    }
    /**
     * handle no-indexable location
     */
    const location = selectedLocation?.location ?? DEFAULT_SEARCH_LOCATION;

    query.set('q', keyword);

    if (selectedLocation?.location) query.set('location', location);

    const search = query.toString() || undefined;

    if (smtOnly) {
      history.push({ pathname: ROUTE_BUILDER.sistaMinutenSerpNoIndex(), search, state });
    } else {
      history.push({ pathname: ROUTE_BUILDER.dealsSerpNoIndex(), search, state });
    }
  };

  const handleOnNoSearchResultsCTA = () =>
    history.push({ pathname: ROUTE_BUILDER.serp(DEFAULT_SEARCH_KEYWORD, DEFAULT_SEARCH_LOCATION) });

  const dealsSearchState = {
    preview: preview
      ? {
          ...preview,
          sections: preview.sections ?? [],
        }
      : {
          sections: [],
        },
  } as Parameters<typeof createSerpCardClickHandlers>[1];

  const { onDealClick, onMerchantClick } = createSerpCardClickHandlers(
    { q: '', type: 'deals_preview' } as BaseSearchManagerOutput,
    dealsSearchState,
    'deals_search_results',
  );

  const hasSearchResults = !isLoading && preview?.sections.some((section) => section.items.length > 0);

  useEffect(() => {
    trackMpEvent({ name: 'screen_shown', properties: { screen_name: 'deals' } });
  }, []);

  const leftSlot = (() => {
    if (isMobileView) {
      if (intersectionTarget.current !== null && intersectionObserver !== null && !isIntersectingHeroSearchbar) {
        return (
          <Link to="/" aria-label="Bokadirekt">
            <Logotype variant="symbol" />
          </Link>
        );
      } else {
        return (
          <Link to="/" aria-label="Bokadirekt" className="w-full">
            <Logotype variant="logo" />
          </Link>
        );
      }
    }
    return undefined;
  })();

  return (
    <div style={{ '--gradient-deals-stop': '480px' } as React.CSSProperties}>
      <PageViewLayout
        type="mainView"
        leftSlot={leftSlot}
        wrapperClass="overflow-x-hidden bg-gradient-deals"
        {...(intersectionTarget.current !== null && intersectionObserver !== null && !isIntersectingHeroSearchbar
          ? {
              middleSlot: (
                <div className="mx-auto max-w-screen-md">
                  <FakeSearchForm ref={searchFormRef} type="deals" onLocationUpdate={handleLocationUpdate} />
                </div>
              ),
            }
          : {
              middleSlot: null,
            })}>
        <HeroSection
          showSearchForm={intersectionTarget.current === null || isIntersectingHeroSearchbar}
          searchFormContainerRef={intersectionTarget}
          searchFormRef={searchFormRef}
          onCategoryClick={handleCategoryClick}
          onLocationUpdate={handleLocationUpdate}
        />

        {/* Sections container */}
        <div className="bg-surfaceColors-surface_l4 py-xs md:py-0">
          <ContainerLayout className="relative" onlyAbove2xl>
            <div className="space-y-xl flex flex-col">
              {isLoading ? (
                <Fragment>
                  <DealsSectionPreviewSkeleton cards={6} />
                  <DealsSectionPreviewSkeleton cards={6} />
                </Fragment>
              ) : (
                <Fragment>
                  {hasSearchResults ? (
                    preview?.sections.map((section) => (
                      <DealsSectionPreview
                        key={section.id}
                        section={section}
                        dispatch={dispatch}
                        onCardClick={({ type, item }) => {
                          if (type === 'deal') onDealClick(item);
                          if (type === 'sistaminuten') onMerchantClick(item);
                        }}
                      />
                    ))
                  ) : (
                    <div className="px-lg pb-4xl">
                      <NoSearchResults onCTAClick={handleOnNoSearchResultsCTA} location={selectedLocation?.location} />
                    </div>
                  )}
                </Fragment>
              )}
            </div>
          </ContainerLayout>
        </div>
      </PageViewLayout>

      <SEO {...seoPropsFromBaseString('deals', `${url.getBaseUrl()}${APP_ROUTES.DEALS}`)} />
    </div>
  );
};

export default function DealsLandingPage() {
  return (
    <BookAppointmentProvider>
      <DealsLanding />
      <BookAppointment />
    </BookAppointmentProvider>
  );
}
